import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Music, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const audioSrc = (url) => (url?.startsWith('/api/') ? `${API_URL}${url}` : url);

const OPTIONS = {
  language: ['Hindi', 'English', 'Hinglish', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Marathi', 'Bengali', 'Gujarati', 'Punjabi', 'Other'],
  voice_type: ['Male', 'Female', 'Kid', 'Character', 'No voice'],
  tone: ['Energetic', 'Calm', 'Emotional', 'Conversational', 'Authoritative', 'Fun / Playful'],
  output_format: ['MP3', 'WAV', 'Both'],
  background_music: ['Yes', 'No'],
};
const LABELS = { language: 'Language', voice_type: 'Voice type', tone: 'Tone', output_format: 'Output format', background_music: 'Background music' };

export const AudioBriefFields = ({ value, onChange }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-4 rounded-xl border border-orange-200 bg-orange-50/40 p-4" data-testid="audio-brief-fields">
      <div className="flex items-center gap-2 text-sm font-semibold text-orange-800"><Music className="h-4 w-4" /> Audio details <span className="text-xs font-normal text-orange-600">(optional)</span></div>
      <div className="space-y-1.5">
        <Label className="text-slate-700 text-sm">Script / lyrics</Label>
        <Textarea value={value.script || ''} onChange={(e) => set('script', e.target.value)} placeholder="Paste the voiceover script or lyrics…" className="min-h-[90px] bg-white" data-testid="audio-script" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.keys(OPTIONS).map((k) => (
          <div key={k} className="space-y-1.5">
            <Label className="text-slate-700 text-sm">{LABELS[k]}</Label>
            <Select value={value[k] || ''} onValueChange={(v) => set(k, v)}>
              <SelectTrigger className="h-10 bg-white" data-testid={`audio-${k}`}><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{OPTIONS[k].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
        <div className="space-y-1.5">
          <Label className="text-slate-700 text-sm">Duration (seconds)</Label>
          <Input type="number" min="1" value={value.duration_seconds || ''} onChange={(e) => set('duration_seconds', e.target.value)} placeholder="e.g. 30" className="h-10 bg-white" data-testid="audio-duration" />
        </div>
      </div>
    </div>
  );
};

export const AudioRefUploadButton = ({ onUploaded }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('Audio file must be 25 MB or smaller'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/tasks/reference-audio`, { method: 'POST', credentials: 'include', body: fd });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Upload failed');
      onUploaded({ type: 'audio', value: body.url, name: body.name });
      toast.success(`Uploaded ${body.name}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  return (
    <>
      <input ref={input} type="file" accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="audio-ref-file-input" />
      <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => input.current?.click()} className="text-orange-700 hover:text-orange-800" data-testid="add-audio-ref-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Upload Audio
      </Button>
    </>
  );
};

export const AudioRefRow = ({ name, url, testId }) => (
  <div className="flex flex-1 flex-wrap items-center gap-3 rounded-md border border-orange-200 bg-orange-50/50 px-3 py-2" data-testid={testId}>
    <Music className="h-4 w-4 shrink-0 text-orange-600" />
    <span className="min-w-0 max-w-[220px] truncate text-sm font-medium text-slate-800" title={name}>{name || 'Audio reference'}</span>
    <audio controls preload="none" src={audioSrc(url)} className="h-9 flex-1 min-w-[220px]" />
  </div>
);

export const AudioDetailsView = ({ details }) => {
  const entries = Object.entries(details || {}).filter(([k, v]) => v && k !== 'script');
  if (!details || (!entries.length && !details.script)) return null;
  const label = (k) => LABELS[k] || (k === 'duration_seconds' ? 'Duration' : k);
  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50/40 p-4 space-y-3" data-testid="audio-details-view">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-orange-700"><Music className="h-3.5 w-3.5" /> Audio details</div>
      {entries.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {entries.map(([k, v]) => (
            <div key={k}><div className="text-[11px] uppercase tracking-wider text-slate-400">{label(k)}</div>
              <div className="text-sm font-medium text-slate-800">{k === 'duration_seconds' ? `${v}s` : v}</div></div>
          ))}
        </div>
      )}
      {details.script && <div><div className="text-[11px] uppercase tracking-wider text-slate-400">Script / lyrics</div><p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{details.script}</p></div>}
    </div>
  );
};
